import { useEffect, useRef, useState } from 'react';
import Button from '@mui/material/Button';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import l from '../../../../../lang';
import { MessageTypes } from '../../../../../constants';
import { Filters } from '../../../../../store/interfaces';
import { useClients } from '../../../../../store/clients';
import { scrollIntoView } from '../../../../../utils';
import Dialog from '../../../../common/dialog';
import Loading from '../../../../common/loading';
import MessageError from '../../../../common/message-error';
import Message from '../../../../common/message';
import ButtonLink from '../../../../common/button-link';
import './styles.scss';

interface DialogExportClientsProps {
  listFilters?: Filters;
}

const DialogExportClients = ({ listFilters }: DialogExportClientsProps) => {
  const {
    export: { loading, error, errorInfo, show, data },
    showExportClients,
    exportClients,
    resetExportClients,
  } = useClients();
  const messageRef = useRef<HTMLDivElement | null>(null);
  const [exportFormat, setExportFormat] = useState<'csv' | 'excel'>('csv');

  const download = (csvData: string) => {
    const extension = exportFormat === 'excel' ? 'xlsx' : 'csv';
    const mimeType =
      exportFormat === 'excel'
        ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        : 'text/csv';
    const blob = new Blob([csvData], { type: `${mimeType};charset=utf-8` });
    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${l('menu.item.CLIENTS')}-dLocalGo.${extension}`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
  };

  useEffect(() => {
    if ((error || data === '') && show) {
      scrollIntoView(messageRef);
    }

    if (data && show) {
      download(data);
    }
  }, [error, data, show]);

  const buildExportFilters = (): Filters => ({
    search: listFilters?.search || '',
    country: listFilters?.country || '',
    sort_by: listFilters?.sort_by || '',
    sort_direction: listFilters?.sort_direction || '',
  });

  const handleClose = () => {
    showExportClients(false);
    resetExportClients();
  };

  const handleExport = () => {
    exportClients(buildExportFilters(), exportFormat);
  };

  const isSuccess = Boolean(data);

  return (
    <div className={`export_clients${isSuccess ? ' export_clients--success' : ''}`}>
      <Dialog
        open={show}
        dividers={false}
        title={<span>{l('clients.export.modal.title')}</span>}
        titleCenter={isSuccess}
        paperClassName={isSuccess ? 'export_clients__dialog-paper' : undefined}
        unpaddingContent
        unpaddingFooter
        type="sm"
        handleClose={handleClose}
        body={
          <div>
            {loading && (
              <div className="export_clients__loading">
                <Loading />
              </div>
            )}
            <div className={`export_clients__content ${loading ? 'export_clients__content__hide' : ''}`}>
              {!data && (
                <div ref={messageRef}>
                  {data === '' && (
                    <div className="export_clients__empty">
                      <Message type={MessageTypes.WARNING} body={l('clients.export.empty')} />
                    </div>
                  )}
                  {error && (
                    <div className="export_clients__error">
                      <MessageError errorInfo={errorInfo} />
                    </div>
                  )}
                </div>
              )}
              {data && (
                <div className="export_clients__success">
                  <div className="export_clients__success-icon" aria-hidden>
                    <FileDownloadIcon />
                  </div>
                  <p>{l('exportTransactions.successMessage')}</p>
                  <p>
                    <span>{l('exportTransactions.downloadAgain')}</span>
                    <ButtonLink text={l('exportTransactions.downloadAgain.here')} onClick={() => download(data)} />
                  </p>
                </div>
              )}
            </div>
            {!data && (
              <div className="export_clients__export_btn">
                <div className="export_clients__format_selector">
                  <span>{l('clients.export.format')}:</span>
                  <label>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="csv"
                      checked={exportFormat === 'csv'}
                      onChange={() => setExportFormat('csv')}
                    />
                    CSV
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="exportFormat"
                      value="excel"
                      checked={exportFormat === 'excel'}
                      onChange={() => setExportFormat('excel')}
                    />
                    XLS
                  </label>
                </div>
                <Button
                  name="exportClients"
                  variant="contained"
                  onClick={handleExport}
                  endIcon={<KeyboardArrowDownIcon />}
                  disabled={loading}
                >
                  {l('exportTransactions.exportBtn')}
                </Button>
              </div>
            )}
          </div>
        }
        footer={isSuccess ? <div className="export_clients__footer" /> : undefined}
      />
    </div>
  );
};

export default DialogExportClients;
